import type { World } from '@/lib/three-d-resources/world'
import { clone } from 'ramda'
import { Box3, PerspectiveCamera, Vector3 } from 'three'
import type { ApiLabel, CameraData, Preview, Vec3Data } from '~/entities/model3d'
import { THREE_D_FOV } from '~/lib/constants/model'
import type { LabelsManager, TextLabel } from '~/lib/three-d-resources/labels-manager'
import type { ThreeDResourceManager } from '~/lib/three-d-resources/three-d-resource-manager'
import { exhaustive } from '~/lib/three-d-resources/utils'

export type LabelSceneVisibility = {
  camera_id: number
  is_visible: boolean
  text_label_id: number
}

export class ConfirmationDeclinedError extends Error {
  constructor() {
    super()
    this.name = 'ConfirmationDeclinedError'
  }
}

export interface UseThreeDRepo {
  save: (params: { title: string }) => Promise<void>
  saveAs: (params: { title: string }) => Promise<number>
  updateSceneConfigTitle: (params: { modelId: number; sceneConfigId: number; title: string }) => Promise<void>
  updateModelTitle: (title: string) => Promise<void>
  updateTextScale: (text_scale: number) => Promise<void>
  updateLabels: (params: { modelId: number; cameraId: number }) => Promise<LabelSceneVisibility[]>
  updateLabelsTextSize: (modelId: number) => Promise<ApiLabel[]>
  createNewCamera: (targetModelId: number) => Promise<CameraData>
  updateSceneVisibility: (
    modelId: number,
    update: { is_visible: boolean; scene_id: number; node_id: number; camera: CameraData },
  ) => Promise<void>
  deckHasV1Models: () => boolean
}

export function useThreeDRepo({
  world,
  threeDResourceManager,
  labelsManager,
  onConfirmLabelsDelete,
  clearLayerGroups,
  onSaved,
}: {
  world: World
  threeDResourceManager: ThreeDResourceManager
  labelsManager: LabelsManager
  onConfirmLabelsDelete?: () => Promise<boolean>
  clearLayerGroups: () => void
  onSaved?: (eventName: 'save3DSlide' | 'saveAs3DSlide') => void
}): UseThreeDRepo {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({ 'X-Application': 'web' }))
  async function save(params: { title: string }) {
    if (!world.currentModel || !world.currentSceneConfig?.id) return

    const savingCameraId = world.currentSceneConfig.id
    const savingModelId = world.currentModel.id

    const camera = world.currentCamera.clone()
    _assertNotSamePosition(camera.position, new Vector3(...world.controls.target))

    const viewData = _getViewData(params)
    await Promise.all([
      $fetch<CameraData>(`v2/3dmodel/${savingModelId}/cameras/${savingCameraId}`, {
        method: 'PATCH',
        ...useFetchSharedOptions(),
        body: viewData,
      }),
      updateLabels({ cameraId: savingCameraId, modelId: savingModelId }),
      world.drillDataSetManager?.updateServer(savingCameraId),
      _updateThumbnail({ title: params.title, cameraId: savingCameraId, modelId: savingModelId }),
    ])
    onSaved?.('save3DSlide')

    const res = await $fetch<CameraData>(`v2/3dmodel/${savingModelId}/cameras/${savingCameraId}`, { ...useFetchSharedOptions() }) // Get the latest data
    const newSceneConfig = threeDResourceManager.createNewSceneConfig(res)

    if (world.currentSceneConfig.id === newSceneConfig.id) {
      labelsManager.setSceneConfig(newSceneConfig)
    }

    await labelsManager.fetchLabelsForModel(world.currentModel.id, world.currentModel.apiLayersById)
  }

  async function saveAs(params: { title: string }): Promise<number> {
    if (!world.currentModel) throw new Error('No currentModel set')

    const savingModelId = world.currentModel.id
    const camera = world.currentCamera.clone()

    _assertNotSamePosition(camera.position, new Vector3(...world.controls.target))

    const viewData = _getViewData(params)
    const result = await $fetch<CameraData>(`v2/3dmodel/${savingModelId}/cameras`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: viewData,
    })
    onSaved?.('saveAs3DSlide')

    const [textLabels] = await Promise.all([
      updateLabels({ cameraId: result.id, modelId: world.currentModel.id }),
      world.drillDataSetManager?.updateServer(result.id),
      _updateThumbnail({ title: result.title, cameraId: result.id, modelId: savingModelId }),
    ])

    world.drillDataSetManager?.setSceneId(result.id)

    result.text_labels = textLabels

    const newSceneConfig = threeDResourceManager.createNewSceneConfig(result)
    if (world.currentSceneConfig?.id === newSceneConfig.id) {
      labelsManager.setSceneConfig(newSceneConfig)
    }
    await labelsManager.fetchLabelsForModel(world.currentModel.id, world.currentModel.apiLayersById)

    return newSceneConfig.id
  }

  async function updateSceneConfigTitle({ modelId, sceneConfigId, title }: { modelId: number; sceneConfigId: number; title: string }) {
    await $fetch(`v2/3dmodel/${modelId}/cameras/${sceneConfigId}`, { ...useFetchSharedOptions(), method: 'PATCH', body: { title } })

    const sceneConfig = threeDResourceManager.sceneConfigs.get(sceneConfigId)
    if (sceneConfig) {
      sceneConfig.title = title
    }
  }

  async function updateModelTitle(title: string) {
    if (!world.currentModel) return

    await $fetch(`v2/3dmodel/${world.currentModel.id}`, { ...useFetchSharedOptions(), method: 'PATCH', body: { title } })
    world.currentModel.title = title
  }

  async function updateTextScale(text_scale: number) {
    if (!world.currentModel) return

    await $fetch(`v2/3dmodel/${world.currentModel.id}`, { ...useFetchSharedOptions(), method: 'PATCH', body: { text_scale } })

    world.currentModel.setTextScale(text_scale)
    labelsManager.setGlobalTextScale(text_scale)
  }

  async function createNewCamera(targetModelId: number) {
    const model = await threeDResourceManager.getModel(targetModelId)
    await threeDResourceManager.loadResourcesForModelId(targetModelId)

    const sceneSize = new Vector3()
    const center = new Vector3()
    const bbox = new Box3().setFromObject(model.scene)
    bbox.getSize(sceneSize)
    bbox.getCenter(center)

    const defaultCamera = new PerspectiveCamera(1, 1, 1, 1)
    defaultCamera.position.copy(new Vector3().addVectors(center, sceneSize))
    defaultCamera.lookAt(center)

    _assert(sceneSize.lengthSq() > 0, 'Scene size must be greater than 0')

    const postData: any = {
      title: model.title,
      position_x: defaultCamera.position.x,
      position_y: defaultCamera.position.y,
      position_z: defaultCamera.position.z,
      rotation_x: defaultCamera.rotation.x,
      rotation_y: defaultCamera.rotation.y,
      rotation_z: defaultCamera.rotation.z,
      rotation_w: 1,
      orientation_x: defaultCamera.quaternion.x,
      orientation_y: defaultCamera.quaternion.y,
      orientation_z: defaultCamera.quaternion.z,
      orientation_w: defaultCamera.quaternion.w,
      x_fov: THREE_D_FOV,
      y_fov: THREE_D_FOV,
      target_x: center.x,
      target_y: center.y,
      target_z: center.z,
    }

    const firstCategoryId = Array.from(model.apiCategoriesById.values())[0]?.id
    const firstLayerInFirstCategory = Array.from(model.apiLayersById.values()).find(layer => layer.parent_id === firstCategoryId)

    switch (model.apiData.version) {
      case 2:
        postData.visible_nodes = [firstLayerInFirstCategory?.id].filter(x => !!x)
        break
      case 1:
        postData.hidden_node_ids = Array.from(model.apiLayersById.values())
          .filter(layer => layer.id !== firstLayerInFirstCategory?.id)
          .map(({ id }) => id)

        break
      default:
        exhaustive(model.apiData.version)
    }

    // Ensure camara position and target are not equal
    const positionVec3 = new Vector3(postData.position_x, postData.position_y, postData.position_z)
    const targetVec3 = new Vector3(postData.target_x, postData.target_y, postData.target_z)

    _assertNotSamePosition(positionVec3, targetVec3)

    const data = await $fetch<CameraData>(`v2/3dmodel/${targetModelId}/cameras`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: postData,
    })

    clearLayerGroups()

    return data
  }

  async function updateLabelsTextSize(modelId: number) {
    const localLabels = clone(labelsManager.getLabels())

    if (!localLabels.length) return []

    const updatedLabels = await $fetch<ApiLabel[]>(`v2/3dmodel/${modelId}/labels`, {
      ...useFetchSharedOptions(),
      method: 'PATCH',
      body: localLabels.map(label => ({
        id: label.persistedId,
        text: label.content,
        offset_x: label.offsetX,
        offset_y: label.offsetY,
        offset_z: label.offsetZ,
        flagpole_visible: label.flagpoleVisible,
        flagpole_index: label.flagpoleIndex,
        position_x: label.point.x,
        position_y: label.point.y,
        position_z: label.point.z,
        node_id: label.nodeId,
        style: { text_size: label.textSize, auto_scale_size: label.autoScaleSize },
        auto_scale: label.autoScale,
      })),
    })

    return updatedLabels
  }

  async function updateLabels({ modelId, cameraId }: { modelId: number; cameraId: number }) {
    const localLabels = clone(labelsManager.getLabels())
    const serverLabels = await $fetch<ApiLabel[]>(`v2/3dmodel/${modelId}/labels`, { ...useFetchSharedOptions() })

    const labelsToBeCreated = localLabels.filter(({ id }) => !serverLabels.find(l => `${l.id}` === id))
    const labelsToBeDeleted = serverLabels.filter(({ id }) => !localLabels.find(l => l.id === `${id}`))
    const labelsToBeUpdated = localLabels.filter(ll => !!serverLabels.find(sl => sl.id === ll.persistedId && _hasLabelBeenUpdated(sl, ll)))

    if (labelsToBeDeleted.length) {
      const confirmed = (await onConfirmLabelsDelete?.()) ?? true

      if (!confirmed) throw new ConfirmationDeclinedError()
    }

    const [newlyCreatedLabels, _deletedLabels, _updatedLabels] = await Promise.all([
      labelsToBeCreated.length
        ? $fetch<ApiLabel[]>(`v2/3dmodel/${modelId}/labels`, {
            ...useFetchSharedOptions(),
            method: 'POST',
            body: labelsToBeCreated.map(label => ({
              model3d_id: modelId,
              node_id: label.nodeId,
              text: label.content,
              position_x: label.point.x,
              position_y: label.point.y,
              position_z: label.point.z,
              flagpole_visible: label.flagpoleVisible,
              flagpole_index: label.flagpoleIndex,
              offset_x: label.offsetX,
              offset_y: label.offsetY,
              offset_z: label.offsetZ,
              style: { text_size: label.textSize, auto_scale_size: label.autoScaleSize },
              auto_scale: label.autoScale,
            })),
          })
        : null,
      labelsToBeDeleted.length
        ? $fetch(`v2/3dmodel/${modelId}/labels`, { ...useFetchSharedOptions(), method: 'DELETE', body: labelsToBeDeleted })
        : null,
      labelsToBeUpdated.length
        ? $fetch<ApiLabel[]>(`v2/3dmodel/${modelId}/labels`, {
            ...useFetchSharedOptions(),
            method: 'PATCH',
            body: labelsToBeUpdated.map(label => ({
              id: label.persistedId,
              text: label.content,
              offset_x: label.offsetX,
              offset_y: label.offsetY,
              offset_z: label.offsetZ,
              flagpole_visible: label.flagpoleVisible,
              flagpole_index: label.flagpoleIndex,
              position_x: label.point.x,
              position_y: label.point.y,
              position_z: label.point.z,
              node_id: label.nodeId,
              style: { text_size: label.textSize, auto_scale_size: label.autoScaleSize },
              auto_scale: label.autoScale,
            })),
          })
        : null,
    ])

    const promises: Promise<LabelSceneVisibility[]>[] = []

    if (newlyCreatedLabels?.length) {
      promises.push(
        $fetch<LabelSceneVisibility[]>(`v2/3dmodel/cameras/${cameraId}/labels`, {
          ...useFetchSharedOptions(),
          method: 'POST',
          body: newlyCreatedLabels.map((l, index) => ({
            camera_id: cameraId,
            is_visible: labelsToBeCreated[index].visible,
            text_label_id: l.id,
          })),
        }),
      )
    }

    const notExistingLabels: TextLabel[] = []
    const existingLabels: TextLabel[] = []

    localLabels.forEach(l => {
      const serverLabel = serverLabels.find(sl => sl.id === l.persistedId)
      if (!serverLabel) return

      const serverVisibility = serverLabel.scene_visibility.find(({ camera_id }) => camera_id === cameraId)

      if (!serverVisibility) {
        notExistingLabels.push(l)
        return
      }

      if (serverVisibility.is_visible !== l.visible) {
        existingLabels.push(l)
      }
    })

    if (notExistingLabels.length) {
      promises.push(
        $fetch<LabelSceneVisibility[]>(`v2/3dmodel/cameras/${cameraId}/labels`, {
          ...useFetchSharedOptions(),
          method: 'POST',
          body: notExistingLabels.map(l => ({
            camera_id: cameraId,
            is_visible: l.visible,
            text_label_id: l.persistedId,
          })),
        }),
      )
    }

    if (existingLabels.length) {
      promises.push(
        $fetch<LabelSceneVisibility[]>(`v2/3dmodel/cameras/${cameraId}/labels`, {
          ...useFetchSharedOptions(),
          method: 'PATCH',
          body: existingLabels.map(l => ({
            camera_id: cameraId,
            is_visible: l.visible,
            text_label_id: l.persistedId,
          })),
        }),
      )
    }

    return await Promise.all(promises).then(results => ([] as LabelSceneVisibility[]).concat(...results))
  }

  async function _updateThumbnail({ title, cameraId, modelId }: { title: string; cameraId: number; modelId: number }) {
    if (!title) return

    const image = new File([await world.getThumbnailBlob()], `${title.substring(0, 90)}.jpg`, { type: 'image/jpeg' })

    const payload = new FormData()

    payload.append('image', image)
    payload.append('camera_id', cameraId.toString())

    const thumbnailId = (await $fetch<Preview[]>(`v2/3dmodel/${modelId}/previews`, { ...useFetchSharedOptions() })).find(
      preview => preview.camera_id === cameraId,
    )?.id

    if (thumbnailId) {
      await $fetch<Preview>(`v2/3dmodel/${modelId}/previews/${thumbnailId}`, {
        method: 'PATCH',
        ...useFetchSharedOptions(),
        body: payload,
      })
    } else {
      await $fetch<Preview>(`v2/3dmodel/${modelId}/previews`, {
        method: 'POST',
        ...useFetchSharedOptions(),
        body: payload,
      })
    }
  }

  function _getViewData({ title }: { title: string }) {
    const [x, y, z] = world.controls.target

    const data = {
      title,
      visible_nodes: undefined as number[] | undefined,
      hidden_node_ids: undefined as number[] | undefined,
      ..._serializeCamera(world.currentCamera.clone(), { x, y, z }),
    }

    const layers = Array.from(world.currentModel!.apiLayersById.values())
    switch (world.currentModel!.apiData.version) {
      case 2:
        data.visible_nodes = layers.filter(l => world.isNodeVisible(l.id)).map(l => l.id)
        break
      case 1:
        data.hidden_node_ids = layers.filter(l => !world.isNodeVisible(l.id)).map(l => l.id)
        break
      default:
        exhaustive(world.currentModel!.apiData.version)
    }

    return data
  }

  async function updateSceneVisibility(
    modelId: number,
    { is_visible, scene_id, node_id, camera }: { is_visible: boolean; scene_id: number; node_id: number; camera: CameraData },
  ) {
    let params

    if (world.currentModel!.apiData.version === 2) {
      const visible_nodes = new Set(Array.from(world.currentModel!.apiLayersById.keys()).filter(id => camera.visible_nodes.includes(id)))

      if (is_visible) {
        visible_nodes.add(node_id)
      } else {
        visible_nodes.delete(node_id)
      }

      params = {
        visible_nodes: Array.from(visible_nodes),
      }
    } else {
      params = {
        hidden_node_ids: is_visible
          ? camera.hidden_nodes.map(x => x.node_id).filter((id: number) => id !== node_id)
          : camera.hidden_nodes.map(x => x.node_id).concat(node_id),
      }
    }

    await $fetch<CameraData>(`v2/3dmodel/${modelId}/cameras/${scene_id}`, { ...useFetchSharedOptions(), method: 'PATCH', body: params })
  }

  function deckHasV1Models(): boolean {
    return threeDResourceManager.hasV1Models()
  }

  return {
    save,
    saveAs,
    updateSceneConfigTitle,
    updateModelTitle,
    updateTextScale,
    updateLabels,
    updateLabelsTextSize,
    createNewCamera,
    updateSceneVisibility,
    deckHasV1Models,
  }
}

function _assert(truth: boolean, message = 'Assertion failed'): void {
  if (!truth) throw new Error(message)
}

function _assertNotSamePosition(cameraPosition: Vector3, targetPosition: Vector3) {
  if (cameraPosition.distanceToSquared(targetPosition) < 1e-5) {
    throw new Error('Camera and target positions are equal')
  }
}

const _serializeCamera = (camera: PerspectiveCamera, target: Vec3Data) => ({
  position_x: camera.position.x,
  position_y: camera.position.y,
  position_z: camera.position.z,
  rotation_x: camera.rotation.x,
  rotation_y: camera.rotation.y,
  rotation_z: camera.rotation.z,
  rotation_w: 1,
  orientation_x: camera.quaternion.x,
  orientation_y: camera.quaternion.y,
  orientation_z: camera.quaternion.z,
  orientation_w: camera.quaternion.w,
  x_fov: camera.fov,
  y_fov: camera.fov,
  target_x: target.x,
  target_y: target.y,
  target_z: target.z,
})

function _hasLabelBeenUpdated(sl: ApiLabel, ll: TextLabel): boolean {
  return (
    sl.text !== ll.content ||
    sl.offset_x !== ll.offsetX ||
    sl.offset_y !== ll.offsetY ||
    sl.offset_z !== ll.offsetZ ||
    sl.flagpole_visible !== ll.flagpoleVisible ||
    sl.flagpole_index !== ll.flagpoleIndex ||
    sl.node_id !== ll.nodeId ||
    sl.position_x !== ll.point.x ||
    sl.position_y !== ll.point.y ||
    sl.position_z !== ll.point.z ||
    sl.style?.text_size !== ll.textSize ||
    sl.style?.auto_scale_size !== ll.autoScaleSize ||
    sl.auto_scale !== ll.autoScale
  )
}
